/**
 * Config accumulator — pure reducer that builds an agent API payload.
 *
 * Both the wizard state machine (Option A) and the LLM engine (Option B)
 * dispatch the same action shapes, so the config is always consistent.
 */

import { WORKSPACE_DEFAULT, decodeChoice, modelPatch } from "../../../utils/agentModel";

export const INITIAL_CONFIG = {
  name: "",
  version: "v1.0",
  channels: [],
  model_choice: WORKSPACE_DEFAULT,   // phase M: workspace default unless a person pins a model
  temperature: 0.7,
  max_tokens: 2048,
  system_prompt: "",
  status: "draft",
  routing_intents: [],
  is_default: false,
  priority: 100,
  // Internal flags (prefixed with _) — stripped before API call
  _guardrails: ["pii", "injection", "output"],
  _escalation_enabled: true,
  _knowledge_enabled: true,
  _tools_enabled: true,
  _allowed_servers: [],
  _routing_enabled: false,
  _orchestration_enabled: false,
  _orchestration_pattern: "sequential",
  _orchestration_agent_ids: [],
};

export function configReducer(state, action) {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "RESET":
      return { ...INITIAL_CONFIG };
    default:
      return state;
  }
}

/**
 * Convert the accumulated config into the full agent API payload,
 * including flow_config with sensible defaults for all pipeline nodes.
 */
export function buildPayload(config, context) {
  const guardrails = config._guardrails || [];
  const escalationEnabled = config._escalation_enabled ?? true;

  const flowConfig = {
    trigger: {
      channels: config.channels,
      name: config.name,
    },
    llm: {
      ...modelPatch(decodeChoice(config.model_choice)).flow_config.llm,
      temperature: config.temperature,
      max_tokens: config.max_tokens,
      system_prompt: config.system_prompt,
    },
    knowledge: {
      enabled: config._knowledge_enabled ?? true,
      top_k: 8,
      score_threshold: 0.30,
    },
    input_guardrails: {
      enabled: guardrails.includes("pii") || guardrails.includes("injection"),
      pii_detection: guardrails.includes("pii"),
      injection_detection: guardrails.includes("injection"),
      blocked_topics: [],
    },
    output_guardrails: {
      enabled: guardrails.includes("output"),
      prohibited_phrases: [],
    },
    condition: {
      enabled: true,
      rules: [
        { intent: "complaint", action: "escalate", label: "Escalate complaints" },
      ],
      default_action: "continue",
      confidence_threshold: 0.6,
    },
    action: {
      send_response: { enabled: true, format: "text" },
      escalation: {
        enabled: escalationEnabled,
        intent_trigger: true,
        history_threshold: 12,
        phrases: [],
        message: "Let me connect you with a human agent.",
      },
      handoff: {
        whatsapp: { mode: "external_webhook", webhook_url: "", auth_header: "", agent_number: "", agent_name: "", confirmation_message: "You've been connected to a human agent." },
        chat: { queue_name: "" },
        voice: { transfer_number: "", ivr_queue_id: "" },
        sms: { mode: "external_webhook", webhook_url: "", auth_header: "", forward_number: "", confirmation_message: "You've been connected to a human agent." },
      },
    },
    api: {
      enabled: config._tools_enabled ?? true,
      max_tool_calls: 5,
      timeout: 30,
      error_handling: "continue",
      allowed_servers: config._allowed_servers || [],
    },
    memory: { enabled: true, history_window: 20, search_type: "recent", index_pattern: "conversations-*" },
    preprocess: {
      language_detection: { enabled: true },
      intent_classification: { enabled: true },
      sentiment_analysis: { enabled: true },
      attachment_processing: { enabled: true },
    },
    function: {},
    grounding: { enabled: true, data_store_ids: [] },
  };

  // Multi-agent orchestration
  if (config._orchestration_enabled && config._orchestration_agent_ids?.length > 0) {
    const agentIds = config._orchestration_agent_ids;
    const pattern = config._orchestration_pattern || "sequential";
    const availableAgents = context?.availableAgents || [];

    flowConfig.orchestration = {
      type: pattern === "parallel" ? "parallel" : "sequential",
      steps: agentIds.map((agentId, i) => {
        const agent = availableAgents.find((a) => a.id === agentId);
        return {
          nodeId: `agent-${i + 1}`,
          agentId,
          agentName: agent?.name || agentId,
          executionMode: pattern,
          inputMapping: { passFullContext: true, customPrompt: "" },
          outputHandling: { mergeStrategy: "replace", outputKey: "agent_response" },
          timeout_ms: 30000,
          errorHandling: { strategy: pattern === "parallel" ? "skip" : "fail", fallbackResponse: "" },
        };
      }),
    };
  }

  return {
    name: config.name,
    version: config.version,
    channels: config.channels,
    temperature: config.temperature,
    max_tokens: config.max_tokens,
    system_prompt: config.system_prompt,
    status: "draft",
    flow_config: flowConfig,
    metadata_json: { canvas: null, ...(config.is_dynamic_agent && { is_dynamic_agent: true }) },
    routing_intents: config.routing_intents || [],
    is_default: config.is_default,
    priority: config.priority,
  };
}
